import * as m from "mithril";
import * as ApiDTOs from "./ApiDtos";
import Either from "../types/Either";
import { ApiAccess, ApiError, ApiMode, IApiClient } from "./IApiClient";


interface HttpJsonResponse<T> {
    status: number,
    body: Either<ApiError, T>,
    isSuccessful: boolean
}


function mapToHttpJsonResponse<T = unknown>(
    xhr: XMLHttpRequest
): HttpJsonResponse<T> {
    try {
        const json = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
            return {
                status: xhr.status,
                body: {
                    right: json as T
                },
                isSuccessful: xhr.status >= 200 && xhr.status < 300,
            };
        } else {
            const error: ApiDTOs.ErrorDTO = json as ApiDTOs.ErrorDTO;
            return {
                status: 500,
                body: {
                    left: {
                        title: error.title,
                        details: error.detail
                    }
                },
                isSuccessful: false,
            };
        }

    } catch (exception) {
        console.error("Fail to parse JSON", exception);
        return {
            status: 500,
            body: {
                left: {
                    title: "unknown",
                    details: "Something went wrong"
                }
            },
            isSuccessful: false,
        };
    }
}


const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
};



export class ApiClient implements IApiClient {
    baseUrl: string;
    apiAccess: ApiAccess | null;

    constructor(baseUrl: string, apiAccess: ApiAccess | null) {
        this.baseUrl = baseUrl;
        this.apiAccess = apiAccess;
    }
    

    setBaseUrl(apiBaseUrl:string) {
        this.baseUrl = apiBaseUrl;
    }

    setApiAccess(apiAccess: ApiAccess) {
        this.apiAccess = apiAccess;
    }

    buildApiUrl(path) {
        return this.baseUrl + path;
    }

    buildAuthorizationHeader(): { [key: string]: string } {
        console.debug("Building Authrization Header :::: ", this.apiAccess)
        if (this.apiAccess == null) {
            console.debug("No Api Access---------------------------")
            return {};
        }
        if (this.apiAccess.type.mode == ApiMode.Direct) {
            return {
                "Authorization": "Bearer " + this.apiAccess.type.bearerToken,
            }
        } else {
            return this.apiAccess.type.headers;
        }

    }

    getMasterData(appId: string): Promise<Either<ApiError, ApiDTOs.MasterDataDTO>> {
        const url = this.buildApiUrl("/public/masterData");
        const urlWithParam = url + "?" + m.buildQueryString({ appId: appId });
        console.log("Getting master data from ", url, "...");
        return m.request({
            method: "GET",
            url: urlWithParam,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.MasterDataDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    getInitialSourceLocations(appId: string): Promise<Either<ApiError, ApiDTOs.SourceLocationDTO[]>> {
        let url = this.buildApiUrl("/public/masterData/sources");
        const urlWithParam = url + "?" + m.buildQueryString({ appId: appId });
        console.log("Getting master data from ", urlWithParam, "...");
        return m.request({
            method: "GET",
            url: urlWithParam,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.SourceLocationDTO[]>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    isReadyApiAccess(): boolean {
        return this.apiAccess != null;
    }

    isReadyApiBaseUrl(): boolean {
        return this.baseUrl != null && this.baseUrl.trim() != "";
    }


    getArticle(slug: string): Promise<Either<ApiError, ApiDTOs.ArticleDTO>> {
        let url = this.buildApiUrl("/articles/" + slug);
        console.log("Getting article data from ", url, "...");
        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.ArticleDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    getOperatorPhotos(operatorId: string): Promise<Either<ApiError, string[]>> {
        let url = this.buildApiUrl("/operators/" + operatorId + "/photos");
        console.log("Getting article data from ", url, "...");
        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<string[]>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    searchTrips(criteria: ApiDTOs.TripSearchCriteria): Promise<Either<ApiError, ApiDTOs.TripResultAggregateDTO>> {

        const url = this.buildApiUrl("/tripSearch") + "?" + m.buildQueryString({
            sourceId: criteria.sourceId,
            destinationId: criteria.destinationId,
            departureDate: criteria.departureDate,
            numberOfSeats: criteria.numberOfSeats,
            isForForeigner: criteria.isForForeigner
        });

        console.log("Searching trips ", url, "...");

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TripResultAggregateDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });


    }

    mockError(): Promise<Either<ApiError, any>> {
        //Mock Error
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    left: {
                        title: "MOCK_ERROR",
                        details: "Mocked error for testing",
                    } as ApiError
                });
            }, 4000);
        });
    }

    getTripDetails(tripId: string): Promise<Either<ApiError, ApiDTOs.TripDTO>> {
        const url = this.buildApiUrl("/trips/" + tripId);

        console.log("Getting trip details for tripId=", tripId, "\nurl: ", url);

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TripDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    createTripCustomization(createRequest: ApiDTOs.TripCustomizationCreateRequest)
        : Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {

        const url = this.buildApiUrl("/tripCustomizations");

        console.log("Create tripCustomization for tripId=", createRequest.tripId, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: createRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TripCustomizationDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    updateTripCustomization(
        tripCustomizationId: string,
        updateRequest: ApiDTOs.TripCustomizationUpdateRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {

        const url = this.buildApiUrl("/tripCustomizations/" + tripCustomizationId);

        console.log("Updating tripCustomization for tripCustomizationId=", tripCustomizationId, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: updateRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TripCustomizationDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    getTripCustomization(
        tripCustomizationId: string,
    ): Promise<Either<ApiError, ApiDTOs.TripCustomizationDTO>> {

        const url = this.buildApiUrl("/tripCustomizations/" + tripCustomizationId);

        console.log("Getting waypoint options for tripCustomizationId=", tripCustomizationId, "\nurl: ", url);

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TripCustomizationDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }


    createTentativeBooking(
        createRequest: ApiDTOs.TentativeBookingCreateRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {

        const url = this.buildApiUrl("/tentativeBookings");
        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: createRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TentativeBookingDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }




    updateTentativeBooking(
        tentativeBookingId: string,
        updateRequest: ApiDTOs.TentativeBookingUpdateRequestDTO

    ): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {

        const url = this.buildApiUrl("/tentativeBookings/" + tentativeBookingId);

        console.log("Updating tentativeBooking for tentativeBookingId=", tentativeBookingId, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: updateRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TentativeBookingDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }


    getTentativeBooking(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.TentativeBookingDTO>> {

        const url = this.buildApiUrl("/tentativeBookings/" + tentativeBookingId);

        console.log("Getting tentativeBooking for tentativeBookingId=", tentativeBookingId, "\nurl: ", url);

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.TentativeBookingDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }


    getPaymentOptions(billId: string): Promise<Either<ApiError, ApiDTOs.PaymentOptionDTO[]>> {
        const url = this.buildApiUrl("/paymentOptions") + "?" + m.buildQueryString({
            billId: billId,
        });

        console.log("Getting payment options for billId=", billId, "\nurl: ", url);

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.PaymentOptionDTO[]>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    verifyPromoCode(
        createRequest: ApiDTOs.PromoCodeVerificationRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.PromoCodeVerifiedResponseDTO>> {
        const url = this.buildApiUrl("/promoCodeVerifications");

        console.log("Verifying promocode for billId=", createRequest.billId, " with promoCode: = ", createRequest.promoCode, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: createRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.PromoCodeVerifiedResponseDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }


    createPaymentIntent(
        paymentInitiationRequest: ApiDTOs.PaymentIntentCreateRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.PaymentIntentDTO>> {
        const url = this.buildApiUrl("/paymentIntents");

        console.log("Creating payment intent for billId=", paymentInitiationRequest.billId, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: paymentInitiationRequest,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.PaymentIntentDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }
    createMiniAppPaymentInitiation(
        request: ApiDTOs.PaymentIntentCreateRequestDTO
    ): Promise<Either<ApiError, ApiDTOs.MiniappPaymentIntentDTO>> {
        const url = this.buildApiUrl("/paymentIntents");

        console.log("Creating payment intent for billId=", request.billId, "\nurl: ", url);

        return m.request({
            method: "POST",
            url: url,
            headers: this.buildAuthorizationHeader(),
            body: request,
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.MiniappPaymentIntentDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    getBookingRequestCreation(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.BookingRequestDTO>> {
        const url = this.buildApiUrl("/paymentOptions");

        console.log("Checking booking request creation for tentativeBookingId=", tentativeBookingId, "\nurl: ", url);

        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.BookingRequestDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    searchBookingRequests(appUserId: string | undefined, pageNo: number, pageSize: number): Promise<Either<ApiError, ApiDTOs.BookingRequestResultsAggregateDTO>> {
        throw new Error("Method not implemented.");
    }
    getBookingRequestDetails(bookingRequestId: string): Promise<Either<ApiError, ApiDTOs.BookingRequestDTO>> {
        throw new Error("Method not implemented.");
    }

    checkBooking(tentativeBookingId: string): Promise<Either<ApiError, ApiDTOs.BookingDTO>> {
        const url = this.buildApiUrl("/my/bookingCheck") + "?" + m.buildQueryString({
            tentativeBookingId: tentativeBookingId
        });;
        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.BookingDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }

    searchBookings(pageNo: number, pageSize: number): Promise<Either<ApiError, ApiDTOs.BookingResultSet>> {
        const url = this.buildApiUrl("/my/bookings") + "?" + m.buildQueryString({
            offset: 0,
            limit: 10,
        });
        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.BookingResultSet>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }
    getBookingDetails(bookingId: string): Promise<Either<ApiError, ApiDTOs.BookingDTO>> {
        const url = this.buildApiUrl("/my/bookings/") + bookingId;
        return m.request({
            method: "GET",
            url: url,
            headers: this.buildAuthorizationHeader(),
            extract: mapToHttpJsonResponse
        }).then((resp: HttpJsonResponse<ApiDTOs.BookingDTO>) => {
            if (resp.isSuccessful && resp.body.right) {
                return { right: resp.body.right };
            } else {
                const error: ApiError = resp.body.left!;
                return { left: error };
            }
        });
    }


}

export { ApiAccess as ApiAccess };